import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createWish } from '../../services/wishesApi';
import { validateWishForm } from '../../utils/validation';
import './WishForm.css';

export interface WishFormData {
  title: string;
  description: string;
  category: string;
  region: string;
  district: string;
  budget: string;
  urgent: boolean;
  userName: string;
  userPhone: string;
  userEmail: string;
}

interface WishFormProps {
  initialData?: Partial<WishFormData>;
  onSubmit?: (data: Omit<WishFormData, 'description' | 'district' | 'budget' | 'userPhone' | 'userEmail'> & {
    description?: string;
    district?: string;
    budget?: string;
    userPhone?: string;
    userEmail?: string;
  }) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  error?: string;
}

const WishForm = ({ initialData, onSubmit, onCancel, isLoading = false, error = '' }: WishFormProps) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<WishFormData>({
    title: initialData?.title || '',
    description: initialData?.description || '',
    category: initialData?.category || '',
    region: initialData?.region || '',
    district: initialData?.district || '',
    budget: initialData?.budget || '',
    urgent: initialData?.urgent || false,
    userName: initialData?.userName || '',
    userPhone: initialData?.userPhone || '',
    userEmail: initialData?.userEmail || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const categories = ['維修', '保養', '美容', '改裝'];
  const regions = ['台北市', '新北市', '桃園市', '台中市', '台南市', '高雄市'];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateWishForm(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    if (onSubmit) {
      // Modal mode: call parent's onSubmit
      const wishData = {
        title: formData.title,
        description: formData.description || undefined,
        category: formData.category,
        region: formData.region,
        district: formData.district || undefined,
        budget: formData.budget || undefined,
        urgent: formData.urgent,
        userName: formData.userName,
        userPhone: formData.userPhone || undefined,
        userEmail: formData.userEmail || undefined,
      };
      await onSubmit(wishData);
    } else {
      // Page mode: handle submission directly
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const wishData = {
          title: formData.title,
          description: formData.description || undefined,
          category: formData.category,
          region: formData.region,
          district: formData.district || undefined,
          budget: formData.budget || undefined,
          urgent: formData.urgent,
          userName: formData.userName,
          userPhone: formData.userPhone || undefined,
          userEmail: formData.userEmail || undefined,
        };

        const newWish = await createWish(wishData);

        if (newWish) {
          navigate(`/wishes/${newWish.id}`);
        } else {
          setSubmitError('發布許願失敗，請稍後再試。');
        }
      } catch {
        setSubmitError('發布許願失敗，請稍後再試。');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCancelClick = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/wishes');
    }
  };

  const displayError = error || submitError;
  const displayIsSubmitting = isLoading || isSubmitting;

  return (
    <div className="wish-form">
      <div className="wish-form__header">
        <h2 className="wish-form__title">發布許願</h2>
        <p className="wish-form__subtitle">填寫您的需求，讓服務商與您聯繫</p>
      </div>

      <form onSubmit={handleSubmit} className="wish-form__form">
        {displayError && (
          <div className="wish-form__error" role="alert">
            {displayError}
          </div>
        )}

        {/* Title */}
        <div className="wish-form__field">
          <label htmlFor="title" className="wish-form__label">
            許願標題 *
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className={`wish-form__input ${
              errors.title ? 'wish-form__input--error' : ''
            }`}
            placeholder="例如：需要汽車定期保養服務"
            disabled={displayIsSubmitting}
          />
          {errors.title && (
            <span className="wish-form__error-text">{errors.title}</span>
          )}
        </div>

        {/* Description */}
        <div className="wish-form__field">
          <label htmlFor="description" className="wish-form__label">
            需求描述
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="wish-form__textarea"
            placeholder="請詳細描述您的需求..."
            rows={4}
            disabled={displayIsSubmitting}
          />
        </div>

        {/* Category & Region */}
        <div className="wish-form__row">
          <div className="wish-form__field">
            <label htmlFor="category" className="wish-form__label">
              服務類別 *
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              className={`wish-form__select ${
                errors.category ? 'wish-form__input--error' : ''
              }`}
              disabled={displayIsSubmitting}
            >
              <option value="">請選擇</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            {errors.category && (
              <span className="wish-form__error-text">{errors.category}</span>
            )}
          </div>

          <div className="wish-form__field">
            <label htmlFor="region" className="wish-form__label">
              地區 *
            </label>
            <select
              id="region"
              name="region"
              value={formData.region}
              onChange={handleChange}
              className={`wish-form__select ${
                errors.region ? 'wish-form__input--error' : ''
              }`}
              disabled={displayIsSubmitting}
            >
              <option value="">請選擇</option>
              {regions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            {errors.region && (
              <span className="wish-form__error-text">{errors.region}</span>
            )}
          </div>
        </div>

        {/* District */}
        <div className="wish-form__field">
          <label htmlFor="district" className="wish-form__label">
            行政區
          </label>
          <input
            type="text"
            id="district"
            name="district"
            value={formData.district}
            onChange={handleChange}
            className="wish-form__input"
            placeholder="例如：信義區"
            disabled={displayIsSubmitting}
          />
        </div>

        {/* Budget */}
        <div className="wish-form__field">
          <label htmlFor="budget" className="wish-form__label">
            預算範圍
          </label>
          <input
            type="text"
            id="budget"
            name="budget"
            value={formData.budget}
            onChange={handleChange}
            className="wish-form__input"
            placeholder="例如：3000-5000"
            disabled={displayIsSubmitting}
          />
          <span className="wish-form__hint">格式：最低-最高（例如：3000-5000）</span>
        </div>

        {/* Urgent */}
        <div className="wish-form__field wish-form__field--checkbox">
          <label className="wish-form__checkbox-label">
            <input
              type="checkbox"
              name="urgent"
              checked={formData.urgent}
              onChange={handleChange}
              className="wish-form__checkbox"
              disabled={displayIsSubmitting}
            />
            <span>這是一個急件</span>
          </label>
        </div>

        {/* Contact Information */}
        <div className="wish-form__section-title">聯絡資訊</div>

        <div className="wish-form__field">
          <label htmlFor="userName" className="wish-form__label">
            聯絡人姓名 *
          </label>
          <input
            type="text"
            id="userName"
            name="userName"
            value={formData.userName}
            onChange={handleChange}
            className={`wish-form__input ${
              errors.userName ? 'wish-form__input--error' : ''
            }`}
            placeholder="請輸入姓名"
            disabled={displayIsSubmitting}
          />
          {errors.userName && (
            <span className="wish-form__error-text">{errors.userName}</span>
          )}
        </div>

        <div className="wish-form__row">
          <div className="wish-form__field">
            <label htmlFor="userPhone" className="wish-form__label">
              聯絡電話
            </label>
            <input
              type="tel"
              id="userPhone"
              name="userPhone"
              value={formData.userPhone}
              onChange={handleChange}
              className={`wish-form__input ${
                errors.userPhone ? 'wish-form__input--error' : ''
              }`}
              placeholder="0912-345-678"
              disabled={displayIsSubmitting}
            />
            {errors.userPhone && (
              <span className="wish-form__error-text">{errors.userPhone}</span>
            )}
          </div>

          <div className="wish-form__field">
            <label htmlFor="userEmail" className="wish-form__label">
              聯絡 Email
            </label>
            <input
              type="email"
              id="userEmail"
              name="userEmail"
              value={formData.userEmail}
              onChange={handleChange}
              className={`wish-form__input ${
                errors.userEmail ? 'wish-form__input--error' : ''
              }`}
              placeholder="your@email.com"
              disabled={displayIsSubmitting}
            />
            {errors.userEmail && (
              <span className="wish-form__error-text">{errors.userEmail}</span>
            )}
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="wish-form__actions">
          <button
            type="button"
            onClick={handleCancelClick}
            className="wish-form__cancel"
            disabled={displayIsSubmitting}
          >
            取消
          </button>
          <button
            type="submit"
            className="wish-form__submit"
            disabled={displayIsSubmitting}
          >
            {displayIsSubmitting ? '發布中...' : '發布許願'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WishForm;
